import React, { useEffect, useRef, useState } from "react";
import { Icon } from "../icons";
import { initials } from "../lib/format";
import { useMcOps } from "../lib/McOpsContext";

const ETA_REQUIRED = "Please enter the ETA date shared by the Long Distance team.";

/**
 * "Assign to Driver Delivery" popup (the prototype's #driverOverlay). The ETA
 * date is the only required field; the optional reminder falls on the ETA date
 * unless the MC picks another.
 */
export const DriverAssignModal = ({ job, onSave, onClose }) => {
  const current = job.driverDelivery || {};
  const [etaDate, setEtaDate] = useState(current.etaDate || "");
  const [etaTime, setEtaTime] = useState(current.etaTime || "");
  const [driverName, setDriverName] = useState(current.driverName || "");
  const [note, setNote] = useState(current.note || "");
  const [reminderText, setReminderText] = useState("");
  const [reminderDate, setReminderDate] = useState("");
  const [showError, setShowError] = useState(false);
  const dateRef = useRef(null);

  useEffect(() => {
    if (dateRef.current) dateRef.current.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const save = () => {
    if (!etaDate) {
      setShowError(true);
      return;
    }
    onSave({ etaDate, etaTime, driverName, note, reminderText, reminderDate });
  };

  return (
    <div
      className="modal-overlay open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal"
        style={{ maxWidth: 480 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="driver-assign-title"
      >
        <div className="modal-head">
          <div className="mh-left">
            <div className="mh-avatar" style={{ background: "var(--indigo)" }}>
              {initials(job.clientName)}
            </div>
            <div>
              <h3 id="driver-assign-title">Assign to Driver Delivery</h3>
              <div className="meta">
                {job.clientName} — {job.zohoId}
              </div>
            </div>
          </div>
          <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <div className="modal-body" style={{ paddingTop: 14 }}>
          <div className="field-row">
            <div className="field">
              <label htmlFor="driver-eta-date">ETA date</label>
              <input
                id="driver-eta-date"
                ref={dateRef}
                type="date"
                value={etaDate}
                aria-required="true"
                aria-invalid={showError && !etaDate ? "true" : undefined}
                onChange={(e) => {
                  setEtaDate(e.target.value);
                  if (e.target.value) setShowError(false);
                }}
              />
            </div>
            <div className="field">
              <label htmlFor="driver-eta-time">ETA time</label>
              <input
                id="driver-eta-time"
                type="time"
                value={etaTime}
                onChange={(e) => setEtaTime(e.target.value)}
              />
            </div>
          </div>
          <div className="field">
            <label htmlFor="driver-name">Driver name/trip number</label>
            <input
              id="driver-name"
              type="text"
              placeholder="Driver's name or trip number"
              value={driverName}
              onChange={(e) => setDriverName(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="driver-note">Note</label>
            <textarea
              id="driver-note"
              rows={3}
              placeholder="e.g. Call client 1 hour before arrival"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="section-label" style={{ marginTop: 4 }}>
            Reminder{" "}
            <span
              className="meta"
              style={{
                textTransform: "none",
                fontWeight: 600,
                letterSpacing: 0,
                color: "var(--ink-400)",
              }}
            >
              — optional
            </span>
          </div>
          <div className="field-row">
            <input
              type="text"
              aria-label="Reminder text"
              placeholder="e.g. Call client 1 hour before arrival"
              value={reminderText}
              onChange={(e) => setReminderText(e.target.value)}
            />
            <input
              type="date"
              aria-label="Reminder date"
              title="Defaults to the ETA date"
              value={reminderDate}
              onChange={(e) => setReminderDate(e.target.value)}
            />
          </div>
          {showError && !etaDate ? (
            <div
              role="alert"
              style={{ color: "var(--danger)", fontSize: "11.5px", fontWeight: 700 }}
            >
              {ETA_REQUIRED}
            </div>
          ) : null}
        </div>
        <div className="modal-foot">
          <button className="btn success" type="button" onClick={save}>
            <Icon name="truck" />
            Assign to Driver Delivery
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/** Mounted once at the page root: shows the popup for `driverAssignJobId`. */
export const DriverAssignHost = () => {
  const { state, actions, driverAssignJobId, closeDriverAssign } = useMcOps();
  const job = driverAssignJobId ? state.jobs.find((j) => j.id === driverAssignJobId) : null;
  if (!job) return null;
  return (
    <DriverAssignModal
      job={job}
      onClose={closeDriverAssign}
      onSave={(details) => {
        actions.assignDriverDelivery(job.id, details);
        closeDriverAssign();
      }}
    />
  );
};
